import { useState, useEffect, useCallback } from 'react';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const slugify = (value) =>
  (value || 'all')
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32);

const buildCacheKey = (prefs, location) => {
  const comfort = slugify(prefs?.comfort || 'dine-out');
  const vibe = slugify(prefs?.vibe || 'all');
  const cuisine = slugify(prefs?.cuisine || 'all');
  const dietary = slugify(prefs?.dietary || 'all');
  const locRaw = prefs?.location || location || 'world';
  const loc = slugify((locRaw || 'world').split(',')[0].trim());
  return `rec_${comfort}_${vibe}_${cuisine}_${dietary}_${loc}`;
};

export const useRecommendations = (initialPreferences) => {
  const { locationString } = useLocation();
  const { user } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [mealPlan, setMealPlan] = useState(null);
  const [aiInsight, setAiInsight] = useState('');
  const [groceryList, setGroceryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aiUpdating, setAiUpdating] = useState(false);
  const [isQuick, setIsQuick] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [hasCache, setHasCache] = useState(false);
  const [activityPrefs, setActivityPrefs] = useState(null);

  // ── 1. Load localStorage cache IMMEDIATELY on mount ─────────────────────
  useEffect(() => {
    const ck = buildCacheKey(initialPreferences, locationString);
    try {
      const stored = localStorage.getItem(ck);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.recommendations?.length) setRecommendations(parsed.recommendations);
        if (parsed.meal_plan) setMealPlan(parsed.meal_plan);
        if (parsed.ai_insight) setAiInsight(parsed.ai_insight);
        if (parsed.grocery_list) setGroceryList(parsed.grocery_list);
        setHasCache(true);
      }
    } catch { /* ignore corrupt cache */ }
  }, []); // eslint-disable-line

  // ── 2. Fetch activity prefs for personalisation (non-blocking) ───────────
  useEffect(() => {
    if (!user) return;
    fetch(`${API_URL}/api/activity/summary`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setActivityPrefs(d); })
      .catch(() => {});
  }, [user]);

  const buildTimeContext = () => {
    const now = new Date();
    const hour = now.getHours();
    let timeOfDay = 'dinner';
    if (hour >= 5 && hour < 11) timeOfDay = 'breakfast';
    else if (hour >= 11 && hour < 15) timeOfDay = 'lunch';
    else if (hour >= 15 && hour < 18) timeOfDay = 'afternoon';
    else if (hour >= 22 || hour < 5) timeOfDay = 'late-night';
    return {
      time_of_day: timeOfDay,
      day_of_week: now.toLocaleDateString('en-US', { weekday: 'long' }),
      local_time: now.toISOString(),
    };
  };

  const resolveDeliveryOptions = (prefs) => {
    if (prefs?.deliveryOptions?.length) return prefs.deliveryOptions;
    const comfort = prefs?.comfort || 'dine-out';
    if (comfort === 'dine-in') return ['delivery'];
    if (comfort === 'cook-at-home') return ['recipe', 'grocery'];
    if (comfort === 'meal-plan') return ['meal-plan', 'meal-kit'];
    return ['dine-in', 'pickup'];
  };

  const resolveEaseOfAccess = (prefs) => {
    const ease = [...(prefs?.easeOfAccess || [])];
    if (prefs?.distance && prefs.distance !== 'all') ease.push(`within ${prefs.distance} km`);
    if (prefs?.openNow) ease.push('open now');
    return Array.from(new Set(ease.filter(Boolean)));
  };

  const buildPayload = (prefs) => {
    const explicitCuisine = prefs?.cuisine && prefs.cuisine !== 'all' ? prefs.cuisine : null;
    const explicitVibe = prefs?.vibe && prefs.vibe !== 'all' ? prefs.vibe : null;
    const cuisineToUse = explicitCuisine || activityPrefs?.top_cuisines?.[0]?.toLowerCase() || 'all';
    const vibeToUse = explicitVibe || (activityPrefs?.top_vibes?.[0]
      ?.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-')) || 'all';
    const timeContext = buildTimeContext();
    return {
      comfort: prefs?.comfort || 'dine-out',
      vibe: vibeToUse,
      cuisine: cuisineToUse,
      dietary: prefs?.dietary || 'all',
      location: prefs?.location || locationString || 'your city',
      session_id: sessionId,
      activity_summary: activityPrefs || null,
      delivery_options: resolveDeliveryOptions(prefs),
      ease_of_access: resolveEaseOfAccess(prefs),
      distance_km: prefs?.distance || null,
      open_now: !!prefs?.openNow,
      price_range: prefs?.priceRange || [],
      ...timeContext,
    };
  };

  // ── 3. Explicit fetch (called by refetch button, NOT auto on mount) ───────
  const fetchRecommendations = useCallback(async (prefs, options = {}) => {
    const { silent = false } = options;
    if (silent) {
      setAiUpdating(true);
    } else {
      setLoading(true);
      setError(null);
    }
    try {
      const payload = buildPayload(prefs);
      const res = await fetch(`${API_URL}/api/recommendations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to fetch recommendations');
      const data = await res.json();

      if (data.recommendations) setRecommendations(data.recommendations);
      if (data.meal_plan) setMealPlan(data.meal_plan);
      if (data.ai_insight) setAiInsight(data.ai_insight);
      if (data.grocery_list) setGroceryList(data.grocery_list);
      if (data.session_id) setSessionId(data.session_id);

      const ck = buildCacheKey(prefs || initialPreferences, payload.location || locationString);
      const cacheData = {
        recommendations: data.recommendations,
        meal_plan: data.meal_plan,
        ai_insight: data.ai_insight,
        grocery_list: data.grocery_list,
      };
      try { localStorage.setItem(ck, JSON.stringify(cacheData)); } catch { /* storage full */ }
      setHasCache(true);
      setIsQuick(false);
    } catch (err) {
      if (!silent) setError(err.message);
    } finally {
      if (silent) {
        setAiUpdating(false);
      } else {
        setLoading(false);
      }
    }
  }, [sessionId, locationString, activityPrefs, initialPreferences]);

  const fetchQuickRecommendations = useCallback(async (prefs) => {
    setLoading(true);
    setError(null);
    try {
      const payload = buildPayload(prefs);
      const res = await fetch(`${API_URL}/api/recommendations/quick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to fetch quick picks');
      const data = await res.json();
      if (data.recommendations) setRecommendations(data.recommendations);
      if (data.meal_plan) setMealPlan(data.meal_plan);
      if (data.ai_insight) setAiInsight(data.ai_insight);
      if (data.grocery_list) setGroceryList(data.grocery_list);
      setIsQuick(true);
    } catch {
      // Silent fail, fall back to full AI request
    } finally {
      setLoading(false);
    }
  }, [sessionId, locationString, activityPrefs, initialPreferences]);
  useEffect(() => {
    const ck = buildCacheKey(initialPreferences, locationString);
    const hasStored = !!localStorage.getItem(ck);
    if (!hasStored) {
      const run = async () => {
        await fetchQuickRecommendations(initialPreferences);
        await fetchRecommendations(initialPreferences, { silent: true });
      };
      run();
    }
  }, []); // eslint-disable-line

  // ── 4. Load More (infinite scroll — appends new batch) ────────────────
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const loadMore = useCallback(async (prefs) => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const payload = buildPayload(prefs);
      // Pass existing titles to avoid duplicates
      payload.exclude_titles = recommendations.map(r => r.name || r.title || '').filter(Boolean);
      const res = await fetch(`${API_URL}/api/recommendations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      const newRecs = data.recommendations || [];
      if (newRecs.length === 0) { setHasMore(false); }
      else { setRecommendations(prev => [...prev, ...newRecs]); }
      if (data.ai_insight) setAiInsight(data.ai_insight);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, recommendations, sessionId, locationString, activityPrefs, initialPreferences]);

  return {
    recommendations, mealPlan, aiInsight, groceryList,
    loading, aiUpdating, isQuick, error, hasCache,
    loadingMore, hasMore, loadMore,
    refetch: fetchRecommendations,
    sessionId,
  };
};
